import { Actor, CollisionType, Color, Circle, Engine, vec, Shape, Animation } from "excalibur";
import { ITEMS, PROGRESSION } from "../config";
import { Macrophage } from "./Macrophage";
import { ItemCollision } from "../systems/CollisionGroups";
import { Resources } from "../resources";
import { createPackedAnimation } from "../utils/AtlasLoader";

/**
 * Shared animation cache for all LipidRaft instances
 * This prevents creating new Animation objects for every pickup spawned
 */
let sharedLipidRaftAnimation: Animation | null = null;
let sharedFallbackGraphic: Circle | null = null;

function getSharedLipidRaftAnimation(): Animation | null {
    if (sharedLipidRaftAnimation) return sharedLipidRaftAnimation;

    try {
        sharedLipidRaftAnimation = createPackedAnimation(
            Resources.LipidRaftImage,
            Resources.LipidRaftJson,
            10 // 10 FPS
        );

        // Scale animation to fit radius
        const targetSize = ITEMS.LIPID_RAFT.RADIUS * 2;
        if (sharedLipidRaftAnimation.width > 0) {
            const scale = targetSize / sharedLipidRaftAnimation.width;
            sharedLipidRaftAnimation.scale = vec(scale, scale);
        }

        return sharedLipidRaftAnimation;
    } catch (error) {
        console.warn('Failed to load lipid_raft animation', error);
        return null;
    }
}

function getSharedFallbackGraphic(): Circle {
    if (!sharedFallbackGraphic) {
        sharedFallbackGraphic = new Circle({
            radius: ITEMS.LIPID_RAFT.RADIUS,
            color: Color.fromHex(ITEMS.LIPID_RAFT.COLOR),
            strokeColor: Color.fromHex('#fef3c7'),
            lineWidth: 3,
        });
    }
    return sharedFallbackGraphic;
}

/**
 * LipidRaft - Health Pick-Up
 * 
 * Floating cholesterol-rich microdomain that restores Membrane Integrity (HP).
 * Based on the Antigen collectible pattern with healing instead of XP.
 * 
 * Scientific Context: Lipid rafts are sub-domains of the plasma membrane
 * containing high concentrations of cholesterol and glycosphingolipids,
 * essential for signal transduction and membrane fluidity.
 */
export class LipidRaft extends Actor {
    private target: Macrophage | null = null;
    private attractSpeed: number = 250;
    private isBeingAttracted: boolean = false;
    private lifetime: number = 15000; // 15 seconds before despawn

    constructor(x: number, y: number) {
        super({
            name: 'LipidRaft',
            pos: vec(x, y),
            radius: ITEMS.LIPID_RAFT.RADIUS,
            color: Color.fromHex(ITEMS.LIPID_RAFT.COLOR),
            collisionType: CollisionType.Passive,
            collisionGroup: ItemCollision,
        });
    }

    override onInitialize(engine: Engine): void {
        // Set up circular collider
        const collider = Shape.Circle(ITEMS.LIPID_RAFT.RADIUS) as any;
        collider.group = ItemCollision;
        this.collider.set(collider);

        // Use shared animation (avoids creating new Animation objects for each pickup)
        const animation = getSharedLipidRaftAnimation();
        if (animation) {
            this.graphics.use(animation);
        } else {
            this.graphics.use(getSharedFallbackGraphic());
        }

        // Auto-destroy after lifetime
        this.actions.delay(this.lifetime).callMethod(() => {
            this.kill();
        });
    }



    override onPreUpdate(_engine: Engine, delta: number): void {
        this.lifetime -= delta;

        if (this.target) {
            const distance = this.target.pos.distance(this.pos);

            // Collect when close enough
            if (distance < PROGRESSION.PICKUP_RADIUS) {
                this.collect();
                return;
            }

            // Attract toward player when in range
            if (this.isBeingAttracted || distance < PROGRESSION.MAGNET_RADIUS) {
                this.isBeingAttracted = true;
                const direction = this.target.pos.sub(this.pos).normalize();
                this.vel = direction.scale(this.attractSpeed);
            }
        }

        // Gentle floating animation (bob up and down)
        const bobAmount = Math.sin(Date.now() / 500) * 2;
        this.graphics.offset = vec(0, bobAmount);
    }

    /**
     * Heal the player and destroy this pickup
     */
    private collect(): void {
        if (this.target) {
            const healAmount = Math.floor(this.target.maxHp * ITEMS.LIPID_RAFT.HEAL_PERCENT);
            this.target.heal(healAmount);
        }
        this.kill();
    }

    /**
     * Set the target for this pickup to be attracted to
     */
    public setTarget(target: Macrophage): void {
        this.target = target;
    }
}
